/*
    news.js
    microCMS「サイト共通(site)」の「お知らせ(news)」繰り返し欄を
    新しい順に並べ、URL 用の id（例: 20261002, 20261002-2）を付けます。
*/

const ymd = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Tokyo", year: "numeric", month: "2-digit", day: "2-digit",
});

const toId = (date) => (date ? ymd.format(new Date(date)).replace(/-/g, "") : "nodate");

export function formatDate(date) {
    if (!date) return "";
    return ymd.format(new Date(date)).replace(/-/g, ".");
}

export function getNewsList(site) {
    const raw = Array.isArray(site?.news) ? site.news : [];
    const sorted = raw
        .filter((n) => n && n.title)
        .slice()
        .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
    const counts = {};
    return sorted.map((n) => {
        const base = toId(n.date);
        counts[base] = (counts[base] || 0) + 1;
        return { ...n, id: counts[base] > 1 ? `${base}-${counts[base]}` : base };
    });
}

/*
    タイトル先頭の【コラム】【ご案内】などをカテゴリーとして取り出します。
    例: "【コラム】集団が苦手な子の…" → { tag: "コラム", text: "集団が苦手な子の…" }
    【６月】のような月表記はカテゴリーにせず「イベント」として扱います。
*/
export function splitTitle(title = "") {
    // 英訳されたタイトルの先頭の [Column] などは表示から外します（ラベルで表示するため）
    const en = title.match(/^\s*\[(Column|Announcement|Information|Notice|News|Event|Events|Activity Report)\]\s*/i);
    if (en) return { tag: "", text: title.slice(en[0].length) };
    const m = title.match(/^\s*【([^】]+)】\s*/);
    if (!m) return { tag: "", text: title };
    const inner = m[1].trim();
    if (/^[0-9０-９]+月$/.test(inner)) return { tag: "イベント", text: title };
    return { tag: inner, text: title.slice(m[0].length) };
}

/*
    microCMS の「ラベル」欄（セレクトフィールド、フィールドID: label）の値を返します。
    未選択のときは "" を返します。
*/
export function getLabel(item) {
    const v = item?.label;
    const s = Array.isArray(v) ? v[0] : v;
    return typeof s === "string" ? s.trim() : "";
}

/*
    カテゴリーの決め方（上から順に優先）
    1. microCMS の「ラベル」欄で選んだもの
    2. タイトル先頭の【コラム】【ご案内】など
    3. 内容からおおまかに分類（開催・セミナーなど → 活動報告、それ以外 → お知らせ）
*/
export function getCategory(item) {
    const label = getLabel(item);
    if (label) return label;
    const { tag } = splitTitle(item?.title);
    if (tag) return tag;
    const t = item?.title || "";
    if (/開催|セミナー|交流会|講座|視察|訪問/.test(t)) return "活動報告";
    return "お知らせ";
}

const TAG_CLASS = {
    "コラム": "is-column",
    "ご案内": "is-guide",
    "イベント": "is-event",
    "活動報告": "is-report",
};
export function getCategoryClass(cat) {
    return TAG_CLASS[cat] || "is-info";
}

/* 英語表示のときのラベル名 */
const CATEGORY_EN = {
    "お知らせ": "News",
    "活動報告": "Activity Report",
    "コラム": "Column",
    "イベント": "Event",
    "ご案内": "Information",
};
export function getCategoryText(cat, lang) {
    return String(lang || "").startsWith("en") ? CATEGORY_EN[cat] || cat : cat;
}

/* 本文 HTML から一覧用の短い抜粋を作ります */
export function getExcerpt(html = "", length = 70) {
    const text = String(html)
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/g, " ")
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/https?:\/\/\S+/g, "")
        .replace(/\s+/g, " ")
        .trim();
    return text.length > length ? text.slice(0, length) + "…" : text;
}

/*
    本文中にそのまま書かれた URL（https://〜）をリンクにします。
    すでに <a> になっている部分はそのまま。外部サイトは新しいタブで開きます。
*/
const URL_RE = /https?:\/\/[^\s<>"'（）「」『』、。，．！？]+/g;
const TRAILING_RE = /[)\]}.,;:!?]+$/;

function isExternal(href) {
    try {
        return new URL(href, window.location.href).origin !== window.location.origin;
    } catch (e) {
        return true;
    }
}

export function linkifyHtml(html = "") {
    if (!html || typeof window === "undefined" || typeof DOMParser === "undefined") return html;
    const doc = new DOMParser().parseFromString(`<div id="__root">${html}</div>`, "text/html");
    const root = doc.getElementById("__root");
    if (!root) return html;

    const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const targets = [];
    while (walker.nextNode()) {
        const node = walker.currentNode;
        if (node.parentElement && node.parentElement.closest("a, script, style")) continue;
        URL_RE.lastIndex = 0;
        if (URL_RE.test(node.nodeValue)) targets.push(node);
    }

    targets.forEach((node) => {
        const text = node.nodeValue;
        const frag = doc.createDocumentFragment();
        let last = 0;
        URL_RE.lastIndex = 0;
        let m;
        while ((m = URL_RE.exec(text))) {
            let url = m[0];
            const trail = (url.match(TRAILING_RE) || [""])[0];
            if (trail) url = url.slice(0, -trail.length);
            if (m.index > last) frag.appendChild(doc.createTextNode(text.slice(last, m.index)));
            const a = doc.createElement("a");
            a.href = url;
            a.textContent = url;
            frag.appendChild(a);
            last = m.index + url.length;
        }
        if (last < text.length) frag.appendChild(doc.createTextNode(text.slice(last)));
        node.parentNode.replaceChild(frag, node);
    });

    // 外部リンクは新しいタブで開く（既存のリンクも含む）
    root.querySelectorAll("a[href]").forEach((a) => {
        if (isExternal(a.getAttribute("href"))) {
            a.setAttribute("target", "_blank");
            a.setAttribute("rel", "noopener noreferrer");
        }
    });
    return root.innerHTML;
}

/* 一覧・ナビ用のサムネイル URL（microCMS の画像変換で軽量化） */
export function getThumb(item, width = 640) {
    const url = item?.image?.url;
    if (!url) return "";
    return `${url}?w=${width}&fm=webp&q=75`;
}
